import { DivConst } from 'constants/divConst';

import type { useApiState } from 'hooks/useApiState';
import type { AcctStateType } from 'stores/atoms/acctState';
import type { FormResponseType } from 'types/api/formResponseType';
import type { FormEditFormStateType } from 'types/page/formEditFormStateType';
import { getProductionUrl } from 'utils/terms';

// ------------------------------------------------------------------ //
// 型定義
// ------------------------------------------------------------------ //

type UseApiStateReturn = ReturnType<typeof useApiState>;

/**
 * postFormRegister の引数型から companyId / formStatusDiv を除いた共通パラメータ型。
 * postFormUpdate へのスプレッドにも利用する。
 */
export type FormCommonParam = Omit<
  Parameters<UseApiStateReturn['postFormRegister']>[0],
  'companyId' | 'formStatusDiv'
>;

/**
 * postFormAiQuestions の request（currentContent）型。
 */
export type FormAiContentInput = Parameters<
  UseApiStateReturn['postFormAiQuestions']
>[0]['currentContent'];

/**
 * postFormAiQuestions のレスポンス（answerContent）から取り出した問題アイテム型。
 */
export type AiFormQuestion = NonNullable<
  Awaited<ReturnType<UseApiStateReturn['postFormAiQuestions']>>['answerContent']
>['questions'][number];

// ------------------------------------------------------------------ //
// convertFormValuesToParam
// ------------------------------------------------------------------ //

/**
 * select / radio 由来の文字列値を number | null に型強制する。
 * HTML フォームは select の value を常に文字列で返すため、API スキーマ（number | null）に揃える。
 * - null / undefined / 空文字 → null
 * - それ以外 → Number() で数値化
 */
function toNumberOrNull(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  return Number(value);
}

/**
 * react-hook-form のフォーム値を API 送信パラメータに変換する。
 * - popupImageKey は含まない（hook 側で別途セットする）
 * - questionImageKey はファルシー値を null に変換する
 * - lineFlag は DivConst の数値から boolean に変換する
 * - options は title が空（trim後）のものを除外する
 * - maxCount / minCount / placeholder は設問タイプに応じて設定する
 * - respondentFields の div 値は toNumberOrNull で number | null に統一する
 *   （HTML select は値を文字列で返すため、API スキーマ型に合わせて変換する）
 */
export function convertFormValuesToParam(values: FormEditFormStateType): FormCommonParam {
  const { questionType } = DivConst.items;

  return {
    title: values.title ?? null,
    titleHiddenFlag: values.titleHiddenFlag ?? false,
    description: values.description ?? null,
    respondentMailDiv: values.respondentMailDiv ?? DivConst.items.formRespondentMail.none.value,
    imageKey: values.imageKey ?? null,
    reportMailFlag: values.reportMailFlag ?? false,
    reportMailSubject: values.reportMailSubject ?? null,
    reportMailSenderName: values.reportMailSenderName ?? null,
    reportMailSignatureHtml: values.reportMailSignatureHtml ?? null,
    reportMailDescribeHtml: values.reportMailDescribeHtml ?? null,
    formTemplateId: values.formTemplateId ?? null,
    publishStartAt: values.publishStartAt ?? null,
    publishEndAt: values.publishEndAt ?? null,
    noIndexFlag: values.noIndexFlag ?? false,
    benchmarkContactListId: values.benchmarkContactListId ?? null,
    gtmId: values.gtmId ?? null,
    respondentFields: values.respondentFields.map(field => ({
      getFlag: field.getFlag,
      respondentFieldDiv: toNumberOrNull(field.respondentFieldDiv),
      respondentStandardFieldDiv: toNumberOrNull(field.respondentStandardFieldDiv),
      label: field.label ?? null,
      placeholder: field.placeholder ?? null,
      respondentCustomFieldDiv: toNumberOrNull(field.respondentCustomFieldDiv),
      respondentCustomFieldInputDiv: toNumberOrNull(field.respondentCustomFieldInputDiv),
      maxLength: toNumberOrNull(field.maxLength),
    })),
    lineFlag: Number(values.lineFlag) === DivConst.items.lineFlag.enable.value,
    lineUrl: values.lineUrl ?? null,
    questions: values.questions.map(question => {
      const qType = Number(question.questionType);
      const questionImageKey = question.questionImageKey || null;

      // 設問タイプ別のパラメータ設定
      // eslint-disable-next-line no-useless-assignment -- 設問タイプ分岐前のデフォルト値として必要
      let maxCount: number | null = null;
      // eslint-disable-next-line no-useless-assignment -- 設問タイプ分岐前のデフォルト値として必要
      let minCount: number | null = null;
      // eslint-disable-next-line no-useless-assignment -- 設問タイプ分岐前のデフォルト値として必要
      let placeholder: string | null = null;
      let requiredFlag = question.requiredFlag ?? false;

      if (qType === questionType.text.value) {
        maxCount = question.textMaxLength ? Number(question.textMaxLength) : null;
        minCount = null;
        placeholder = question.placeholder ?? null;
      } else if (qType === questionType.numeric.value) {
        maxCount = question.numberMaxLength ? Number(question.numberMaxLength) : null;
        minCount = null;
        placeholder = null;
      } else if (qType === questionType.checkbox.value) {
        maxCount = question.checkboxMaxCount ? Number(question.checkboxMaxCount) : null;
        minCount = question.checkboxMinCount ? Number(question.checkboxMinCount) : null;
        placeholder = null;
        requiredFlag = false;
      } else {
        // radio / select / image
        maxCount = null;
        minCount = null;
        placeholder = null;
      }

      const options = question.options
        .filter(option => option.title?.trim())
        .map(option => ({
          title: option.title ?? null,
          imageKey: option.imageKey || null,
        }));

      return {
        title: question.title ?? null,
        questionImageKey,
        questionType: qType,
        minCount,
        maxCount,
        placeholder,
        requiredFlag,
        options,
      };
    }),
    results: values.results.map(result => ({
      title: result.title ?? null,
      descriptionHtml: result.descriptionHtml ?? null,
      imageKey: result.imageKey ?? null,
    })),
  };
}

// ------------------------------------------------------------------ //
// buildInitialFormForm
// ------------------------------------------------------------------ //

/**
 * 新規フォーム作成時のフォーム初期値を生成する。
 * reportMailSenderAddress は hook 側で config.ses.from.address から設定するため含まない。
 */
export function buildInitialFormForm(acct: AcctStateType): Partial<FormEditFormStateType> {
  const company = acct.company;
  const { questionDefault, termsInputType, formRespondentMail, questionType } = DivConst.items;

  const termsUrl =
    company.termsInputDiv === termsInputType.url.value
      ? (company.termsUrl ?? '')
      : getProductionUrl(company.cd);

  return {
    privacyPolicyUrl: company.privacyPolicyUrl ?? '',
    termsUrl,
    copyright: company.copyright ?? '',
    reportMailReplyAddress: company.reportMailReplyAddress ?? '',
    reportMailSenderName: company.reportMailSenderName ?? '',
    reportMailSignatureHtml: company.reportMailSignatureHtml ?? '',
    publishStartAt: null,
    publishEndAt: null,
    noIndexFlag: false,
    formTemplateId: 1,
    respondentMailDiv: formRespondentMail.beginning.value,
    reportMailFlag: true,
    respondentFieldGroupId: null,
    respondentFields: [],
    popupImageKey: null,
    questions: [
      {
        title: '',
        questionImageKey: undefined,
        questionType: questionType.text.value.toString(),
        placeholder: '',
        requiredFlag: questionDefault.defaultRequiredFlag,
        textMaxLength: questionDefault.textMaxLength,
        numberMaxLength: questionDefault.numberMaxLength,
        checkboxMinCount: questionDefault.checkboxMinCount,
        checkboxMaxCount: questionDefault.checkboxMaxCount,
        options: [{ title: '' }, { title: '' }, { title: '' }],
      },
    ],
    results: [
      {
        id: Math.random(),
        title: '',
        descriptionHtml: '',
        imageKey: undefined,
        isNew: true,
      },
    ],
  };
}

// ------------------------------------------------------------------ //
// buildEditFormFromResponse
// ------------------------------------------------------------------ //

/**
 * API レスポンスをフォーム state に変換する（編集画面初期化用）。
 * - lineFlag は boolean → DivConst の数値に変換する
 * - respondentFields は id / no を含めてそのままマッピングする
 * - questions は設問タイプに応じてフォーム用フィールドに変換する
 * - results は id を含めてマッピングする
 */
export function buildEditFormFromResponse(
  response: FormResponseType
): Partial<FormEditFormStateType> {
  const { questionType, questionDefault, lineFlag: lineFlagConst } = DivConst.items;

  return {
    title: response.title ?? undefined,
    titleHiddenFlag: response.titleHiddenFlag,
    description: response.description ?? undefined,
    respondentMailDiv: response.respondentMailDiv ?? undefined,
    imageKey: response.imageKey ?? undefined,
    popupImageKey: response.popupImageKey,
    reportMailFlag: response.reportMailFlag,
    reportMailSubject: response.reportMailSubject ?? undefined,
    reportMailSenderName: response.reportMailSenderName ?? undefined,
    reportMailSignatureHtml: response.reportMailSignatureHtml ?? undefined,
    reportMailDescribeHtml: response.reportMailDescribeHtml ?? undefined,
    formTemplateId: response.formTemplateId ?? undefined,
    publishStartAt: response.publishStartAt ?? null,
    publishEndAt: response.publishEndAt ?? null,
    noIndexFlag: response.noIndexFlag,
    benchmarkContactListId: response.benchmarkContactListId ?? undefined,
    gtmId: response.gtmId ?? undefined,
    respondentFieldGroupId: response.respondentFieldGroupId ?? undefined,
    respondentFields: response.respondentFields.map(field => ({
      ...field,
    })) as unknown as FormEditFormStateType['respondentFields'],
    lineFlag: response.lineFlag ? lineFlagConst.enable.value : lineFlagConst.disable.value,
    lineUrl: response.lineUrl ?? undefined,
    questions: response.questions.map(q => {
      const qType = q.questionType;

      // 設問タイプ別のデフォルト値
      const base = {
        title: q.title ?? undefined,
        questionImageKey: q.questionImageKey ?? undefined,
        questionType: qType.toString(),
        placeholder: q.placeholder ?? undefined,
        requiredFlag: q.requiredFlag,
        textMaxLength: questionDefault.textMaxLength,
        numberMaxLength: questionDefault.numberMaxLength,
        checkboxMinCount: questionDefault.checkboxMinCount,
        checkboxMaxCount: questionDefault.checkboxMaxCount,
      };

      let typeSpecific: Partial<typeof base> = {};

      if (qType === questionType.text.value) {
        typeSpecific = {
          textMaxLength:
            q.maxCount !== undefined ? q.maxCount.toString() : questionDefault.textMaxLength,
          placeholder: q.placeholder ?? undefined,
        };
      } else if (qType === questionType.numeric.value) {
        typeSpecific = {
          numberMaxLength:
            q.maxCount !== undefined ? q.maxCount.toString() : questionDefault.numberMaxLength,
        };
      } else if (qType === questionType.checkbox.value) {
        typeSpecific = {
          checkboxMaxCount:
            q.maxCount !== undefined ? q.maxCount.toString() : questionDefault.checkboxMaxCount,
          checkboxMinCount:
            q.minCount !== undefined ? q.minCount.toString() : questionDefault.checkboxMinCount,
        };
      }

      const options = q.options.map(opt => ({
        title: opt.title ?? undefined,
        imageKey: opt.imageKey ?? undefined,
      }));

      return Object.assign({}, base, typeSpecific, { options });
    }),
    results: response.results.map(r => ({
      id: r.id,
      title: r.title ?? undefined,
      descriptionHtml: r.descriptionHtml ?? undefined,
      imageKey: r.imageKey ?? undefined,
      isNew: false,
    })),
  };
}

// ------------------------------------------------------------------ //
// buildAiChatCurrentContent
// ------------------------------------------------------------------ //

/**
 * フォーム編集画面のフォーム値を AI チャット送信用の currentContent に変換する。
 * - 出力キーは FormAiContentInput の全キーのみ（state 余剰キーは含めない）
 * - 未設定値は `?? null` で必ずキーを送る（`?? undefined` は JSON.stringify でキーが落ちて 400 になるため禁止）
 * - questions に `name: 設問N`（N は 1 始まり）を付与する
 * - questionType / textMaxLength / numberMaxLength / checkboxMinCount / checkboxMaxCount は
 *   state の文字列値を Number() で number に変換する（空文字・未設定は null）
 * - questionImageKey / options[].imageKey / results[].imageKey はフォームのみ契約に含めて送る
 */
export function buildAiChatCurrentContent(values: FormEditFormStateType): FormAiContentInput {
  return {
    title: values.title ?? null,
    description: values.description ?? null,
    questions: values.questions.map((question, index) => ({
      name: `設問${index + 1}`,
      title: question.title ?? null,
      questionImageKey: question.questionImageKey ?? null,
      questionType: toNumberOrNull(question.questionType),
      placeholder: question.placeholder ?? null,
      requiredFlag: question.requiredFlag ?? null,
      textMaxLength: toNumberOrNull(question.textMaxLength),
      numberMaxLength: toNumberOrNull(question.numberMaxLength),
      checkboxMinCount: toNumberOrNull(question.checkboxMinCount),
      checkboxMaxCount: toNumberOrNull(question.checkboxMaxCount),
      options: question.options.map(option => ({
        title: option.title ?? null,
        imageKey: option.imageKey ?? null,
      })),
    })),
    results: values.results.map(result => ({
      title: result.title ?? null,
      descriptionHtml: result.descriptionHtml ?? null,
      imageKey: result.imageKey ?? null,
    })),
  };
}

// ------------------------------------------------------------------ //
// mapAiQuestionToFormQuestion
// ------------------------------------------------------------------ //

/**
 * AI 応答の単一設問（answerContent 経由）をフォーム questions 形式に変換する。
 * 全体生成・部分修正（パッチ）の両方から使用する。
 * - questionType / textMaxLength / numberMaxLength / checkboxMinCount / checkboxMaxCount は
 *   既定値を数値で確定してから .toString() で文字列に変換する
 */
export function mapAiQuestionToFormQuestion(
  question: AiFormQuestion
): FormEditFormStateType['questions'][number] {
  return {
    title: question.title ?? undefined,
    questionImageKey: question.questionImageKey ?? null,
    questionType: (question.questionType ?? 1).toString(),
    placeholder: question.placeholder ?? '',
    requiredFlag: question.requiredFlag ?? false,
    textMaxLength: (question.textMaxLength ?? 100).toString(),
    numberMaxLength: (question.numberMaxLength ?? 20).toString(),
    checkboxMinCount: (question.checkboxMinCount ?? 0).toString(),
    checkboxMaxCount: (question.checkboxMaxCount ?? 3).toString(),
    options: question.options.map(option => ({
      title: option.title ?? undefined,
      imageKey: option.imageKey ?? null,
    })),
  };
}
